import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  DialogContentText,
  Grid,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import moment from "moment-timezone";
import useJobPickups from "../../../hooks/useJobPickups";
import { useDebounce } from "use-debounce";
import { useInView } from "react-intersection-observer";
import ListFooter from "../../../components/InfiniteScroller/ListFooter";
import TextFieldDebounced from "../../../components/TextFieldDebounced";
import InfiniteScrollerTable from "../../../components/InfiniteScroller/InfiniteScrollerTable";
import SearchPickupHistoryByJobDialog from "./SearchPickupHistoryByJobDialog";
import SendNotificationByJobDialog from "./SendNotificationByJobDialog";
import SendPickupAvailableNotificationDialog from "./SendPickupAvailableNotificationDialog";

const DEFAULT_NOTIFICATION_DIALOG_DATA = {
  open: false,
  orderId: null,
};
const JobPickups = ({ search, status }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [sendNotificationDialogData, setSendNotificationDialogData] = useState(
    DEFAULT_NOTIFICATION_DIALOG_DATA,
  );

  const [loading, setLoading] = useState(false);
  const [searchPickupHistoryDialogOpen, setSearchPickupHistoryDialogOpen] =
    useState(false);

  const [sendNotificationByJobDialogOpen, setSendNotificationByJobDialogOpen] =
    useState(false);

  const { authToken, ezorder } = useContext(AuthContext);
  const [jobs, onRefresh, refreshing, onLoadMore, loadingMore, total, hasMore] =
    useJobPickups(authToken, 50, null, search, status);

  const [ref, inView, entry] = useInView({
    /* Optional options */
    threshold: 0.5, //Number between 0 and 1 indicating the percentage that should be visible before triggering. Can also be an array of numbers, to create multiple trigger points.
  });

  const [inViewDebounce] = useDebounce(inView, 1000);

  const updateSearch = (searchUpdate, statusUpdate) => {
    let query = [];
    if (searchUpdate) {
      query.push(`search=${searchUpdate}`);
    }
    if (statusUpdate) {
      query.push(`status=${statusUpdate}`);
    }
    if (query.length > 0) {
      navigate(`/job-pickups?${query.join("&")}`); // search=hello&status=DRAFT
    } else {
      navigate(`/job-pickups`);
    }
  };

  useEffect(() => {
    if (jobs.length > 0 && inView) {
      console.log("Footer Element In View. Load More...");
      onLoadMore();
    }
  }, [inViewDebounce]);

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", flexDirection: "column" }}
    >
      <Typography variant="h4" component="div">
        Job PIckups
      </Typography>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: "16px",
          marginBottom: "16px",
        }}
      >
        <Box>
          <Button
            size="large"
            variant="outlined"
            onClick={() => {
              setSearchPickupHistoryDialogOpen(true);
            }}
          >
            Pickup History By Job
          </Button>
          <Button
            sx={{ marginLeft: "8px" }}
            size="large"
            variant="outlined"
            onClick={() => {
              setSendNotificationByJobDialogOpen(true);
            }}
          >
            Send Notification
          </Button>
        </Box>
        <Box sx={{ marginLeft: "8px", flex: 1 }}>
          <TextFieldDebounced
            size="small"
            fullWidth
            variant="outlined"
            label="Search"
            value={search}
            onChange={(e) => {
              updateSearch(e.target.value, status);
            }}
          />
        </Box>
        <TextField
          size="small"
          sx={{ width: "200px", marginLeft: "8px" }}
          select
          label="Status"
          value={status}
          onChange={(e) => {
            updateSearch(search, e.target.value);
          }}
        >
          <MenuItem value={"PENDING"}>Ready For Pickup</MenuItem>
          <MenuItem value={"ALL"}>All</MenuItem>
        </TextField>
      </Box>
      <InfiniteScrollerTable
        TableContainerComponent={Paper}
        // TableStyle={{ minWidth: "600px" }}
        TableHead={() => (
          <TableHead>
            <TableRow>
              <TableCell align="left">Pickup ID</TableCell>
              <TableCell align="left">Project</TableCell>
              <TableCell align="left">Customer</TableCell>
              <TableCell align="left">Order</TableCell>
              {/* <TableCell align="left">Status</TableCell> */}
              <TableCell align="left">Created On</TableCell>
              <TableCell align="left">Completed On</TableCell>
              <TableCell align="left">Completed By</TableCell>
            </TableRow>
          </TableHead>
        )}
        items={jobs}
        renderRow={(item, index) => {
          return (
            <>
              <TableRow
                align="left"
                key={item.id}
                hover
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  cursor: "pointer",
                }}
              >
                <TableCell
                  align="left"
                  onClick={() => {
                    navigate(
                      `/job-pickups/${item.pickupHistory.order.id}/pickups/${item.id}`,
                    );
                  }}
                >
                  {item.id}
                </TableCell>
                <TableCell
                  align="left"
                  onClick={() => {
                    navigate(
                      `/job-pickups/${item.pickupHistory.order.id}/pickups/${item.id}`,
                    );
                  }}
                >
                  {item.pickupHistory.order.project.name}
                </TableCell>
                <TableCell
                  align="left"
                  onClick={() => {
                    navigate(
                      `/job-pickups/${item.pickupHistory.order.id}/pickups/${item.id}`,
                    );
                  }}
                >
                  {item.pickupHistory.order.company &&
                  item.pickupHistory.order.company.name
                    ? item.pickupHistory.order.company.name
                    : "GUEST"}
                </TableCell>
                <TableCell
                  align="left"
                  onClick={() => {
                    navigate(
                      `/job-pickups/${item.pickupHistory.order.id}/pickups/${item.id}`,
                    );
                  }}
                >
                  Order # {item.pickupHistory.order.id}
                </TableCell>
                {/* <TableCell
                  align="left"
                  onClick={() => {
                     navigate(
                      `/job-pickups/${item.pickupHistory.order.id}/pickups/${item.id}`,
                    );
                  }}
                >
                  {item.pickupStatus}
                </TableCell> */}
                <TableCell
                  align="left"
                  onClick={() => {
                    navigate(
                      `/job-pickups/${item.pickupHistory.order.id}/pickups/${item.id}`,
                    );
                  }}
                >
                  {item.createdAt
                    ? moment(item.createdAt)
                        .tz("America/Phoenix")
                        .format("MM/DD/YY")
                    : ""}
                </TableCell>
                <TableCell
                  align="left"
                  onClick={() => {
                    navigate(
                      `/job-pickups/${item.pickupHistory.order.id}/pickups/${item.id}`,
                    );
                  }}
                >
                  {item.completedOn
                    ? moment(item.completedOn)
                        .tz("America/Phoenix")
                        .format("MM/DD/YY")
                    : ""}
                </TableCell>
                <TableCell
                  align="left"
                  onClick={() => {
                    navigate(
                      `/job-pickups/${item.pickupHistory.order.id}/pickups/${item.id}`,
                    );
                  }}
                >
                  {item.completedBy ?? ""}
                </TableCell>
              </TableRow>
            </>
          );
        }}
        onRefresh={onRefresh}
        onEndReached={onLoadMore}
        loading={refreshing || loadingMore}
        ListFooterComponent={() => {
          return <ListFooter hasMore={hasMore} onClick={onLoadMore} />;
        }}
      />
      <SearchPickupHistoryByJobDialog
        open={searchPickupHistoryDialogOpen}
        // jobDetails={selectedJob}
        handleSelect={(jobId) => {
          setSearchPickupHistoryDialogOpen(false);
        }}
        handleClose={() => {
          setSearchPickupHistoryDialogOpen(false);
        }}
      />

      <SendNotificationByJobDialog
        open={sendNotificationByJobDialogOpen}
        // jobDetails={selectedJob}
        handleSelect={(orderId) => {
          setSendNotificationByJobDialogOpen(false);
          setSendNotificationDialogData({
            open: true,
            orderId: orderId,
          });
        }}
        handleClose={() => {
          setSendNotificationByJobDialogOpen(false);
        }}
      />
      <SendPickupAvailableNotificationDialog
        open={sendNotificationDialogData.open}
        orderId={sendNotificationDialogData.orderId}
        // jobDetails={selectedJob}
        handleClose={() => {
          setSendNotificationDialogData(DEFAULT_NOTIFICATION_DIALOG_DATA);
        }}
      />
    </Box>
  );
};
export default JobPickups;
